import React from 'react';
import { Layers, AlertTriangle, Info } from 'lucide-react';

const borderByRegime = {
  euphoria_caution: 'border-amber-300 bg-amber-50/60',
  pessimism_contrarian: 'border-slate-300 bg-slate-50/80',
  cautious_sentiment: 'border-yellow-200 bg-yellow-50/50',
  balanced: 'border-blue-200 bg-blue-50/40'
};

const PositionScalingSection = ({ framework }) => {
  if (!framework) return null;

  const {
    regime,
    regimeTitle,
    starterTranchePercent,
    signalsTriggered,
    scaleInSteps,
    philosophyNote,
    disclaimer,
    vixSnapshot
  } = framework;
  const boxClass = borderByRegime[regime] || borderByRegime.balanced;

  return (
    <div className={`rounded-lg shadow-sm border-2 p-6 ${boxClass}`}>
      <div className="flex items-start gap-3 mb-4">
        <Layers className="h-6 w-6 text-gray-800 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold text-gray-900">Scale-in framework (chart-first)</h3>
          <p className="text-sm text-gray-700 mt-1">{regimeTitle}</p>
          {vixSnapshot && vixSnapshot.value != null && (
            <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
              <span className="font-semibold text-gray-900">VIX (snapshot):</span>
              <span className="px-2 py-0.5 rounded-md bg-white border border-gray-200 font-mono font-bold text-gray-900">
                {Number(vixSnapshot.value).toFixed(2)}
              </span>
              {vixSnapshot.status && (
                <span className="text-gray-600 capitalize">({String(vixSnapshot.status).toLowerCase()})</span>
              )}
              {vixSnapshot.marketSentiment && (
                <span className="text-gray-600">· broad read: {vixSnapshot.marketSentiment}</span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-white border border-gray-200 text-gray-900">
          Suggested opening tranche: ~{starterTranchePercent}% of max intended
        </span>
        {regime === 'euphoria_caution' && (
          <span className="inline-flex items-center text-xs text-amber-900">
            <AlertTriangle className="h-4 w-4 mr-1" />
            Crowd heat / extension
          </span>
        )}
      </div>

      {signalsTriggered && signalsTriggered.length > 0 && (
        <div className="mb-4">
          <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Signals used</p>
          <ul className="list-disc list-inside text-sm text-gray-800 space-y-1">
            {signalsTriggered.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="mb-4">
        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">How to think about adds</p>
        <ol className="list-decimal list-inside text-sm text-gray-800 space-y-2">
          {(scaleInSteps || []).map((step, i) => (
            <li key={i} className="pl-1">
              {step}
            </li>
          ))}
        </ol>
      </div>

      <div className="flex gap-2 p-3 bg-white/70 rounded-md border border-gray-200 mb-3">
        <Info className="h-5 w-5 text-blue-600 flex-shrink-0" />
        <p className="text-xs text-gray-700">{philosophyNote}</p>
      </div>

      <p className="text-xs text-gray-500">{disclaimer}</p>
    </div>
  );
};

export default PositionScalingSection;
